/**
 * Offline state for the UI: are we connected, what is queued, and when did we last sync.
 *
 * `navigator.onLine` alone is not trustworthy — it reports link state, not reachability,
 * so a captive portal or a dead VPN reads as "online". A cheap poll of `/health` while
 * the browser claims to be offline gives the real answer and recovers the moment the
 * connection actually returns.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { OutboxEntry } from '@ias/shared';

import { useAuth } from '../auth';
import { drainOutbox, listOutbox, subscribeToOutbox } from './outbox';

interface OfflineValue {
  isOnline: boolean;
  entries: OutboxEntry[];
  pendingCount: number;
  failedCount: number;
  isSyncing: boolean;
  lastSyncAt: string | null;
  /** Force a drain now — the "sync" button in the outbox panel. */
  syncNow: () => Promise<void>;
}

const OfflineContext = React.createContext<OfflineValue | null>(null);

/** Reachability probe interval while the browser reports itself offline. */
const PROBE_INTERVAL_MS = 15_000;

export function OfflineProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [isOnline, setIsOnline] = React.useState(() => navigator.onLine);
  const [entries, setEntries] = React.useState<OutboxEntry[]>([]);
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [lastSyncAt, setLastSyncAt] = React.useState<string | null>(null);

  React.useEffect(() => subscribeToOutbox(setEntries), []);

  React.useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // `offline` fires reliably; `online` does not always, and never tells us whether the
  // API is actually reachable. Probe while we believe we are down.
  React.useEffect(() => {
    if (isOnline) return;

    const probe = async () => {
      try {
        const response = await fetch('/health', { method: 'GET', cache: 'no-store' });
        if (response.ok) setIsOnline(true);
      } catch {
        // Still down — keep waiting.
      }
    };

    const timer = setInterval(probe, PROBE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isOnline]);

  const userId = user?.id;

  const sync = React.useCallback(async () => {
    if (!userId || !navigator.onLine) return;

    setIsSyncing(true);
    try {
      const result = await drainOutbox(userId);
      if (result.synced > 0) {
        setLastSyncAt(new Date().toISOString());
        // Everything the queue touches is now stale on the server's side of the fence.
        await queryClient.invalidateQueries();
      }
    } finally {
      setIsSyncing(false);
    }
  }, [userId, queryClient]);

  // Drain when the connection returns, and once on sign-in so a queue left behind by a
  // previous session goes out as soon as its owner is back.
  React.useEffect(() => {
    if (!isOnline || !userId) return;
    void sync();
  }, [isOnline, userId, sync]);

  // A slow retry catches entries that backed off after a transient failure.
  React.useEffect(() => {
    if (!isOnline || !userId) return;
    const timer = setInterval(() => void sync(), 60_000);
    return () => clearInterval(timer);
  }, [isOnline, userId, sync]);

  React.useEffect(() => {
    void listOutbox().then(setEntries);
  }, [userId]);

  const mine = React.useMemo(
    () => entries.filter((entry) => entry.userId === userId),
    [entries, userId],
  );

  const value = React.useMemo<OfflineValue>(
    () => ({
      isOnline,
      entries: mine,
      pendingCount: mine.filter((e) => e.status !== 'failed').length,
      failedCount: mine.filter((e) => e.status === 'failed').length,
      isSyncing,
      lastSyncAt,
      syncNow: sync,
    }),
    [isOnline, mine, isSyncing, lastSyncAt, sync],
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline(): OfflineValue {
  const context = React.useContext(OfflineContext);
  if (!context) throw new Error('useOffline must be used inside <OfflineProvider>');
  return context;
}

/**
 * Guard for online-only affordances.
 *
 * Returns whether the action can run right now plus the i18n key explaining why not, so
 * a disabled button can say "needs a connection — this moves money" instead of just
 * being grey.
 */
export function useOnlineOnly(reasonKey: string): { allowed: boolean; reasonKey: string | null } {
  const { isOnline } = useOffline();
  return { allowed: isOnline, reasonKey: isOnline ? null : reasonKey };
}
